import React, { useEffect, useState } from "react";
import axios from "axios";
import { adminApi, API, fileUrl } from "../../lib/api";
import { useDialog } from "../../lib/dialog";
import { useEvents } from "../../lib/events";
import AdminLayout from "../../components/app/AdminLayout";
import { Card, Loader } from "./Dashboard";
import FieldRenderer from "../../components/app/FieldRenderer";
import {
  Save, Plus, Trash2, ChevronUp, ChevronDown, Eye, ExternalLink, Check, X, AlertCircle, GripVertical, Tag,
  Upload, Image as ImageIcon, Palette,
} from "lucide-react";

const FIELD_TYPES = [
  ["short_text", "Short Text"],
  ["long_text", "Long Text / Paragraph"],
  ["email", "Email"],
  ["number", "Number"],
  ["date", "Date Picker"],
  ["dropdown", "Dropdown"],
  ["multiple_choice", "Multiple Choice (radio)"],
  ["yes_no", "Yes / No"],
  ["checkbox", "Checkbox (consent)"],
  ["file_upload", "File Upload"],
  ["availability_calendar", "Availability Calendar"],
  ["text_block", "Text Block (no input)"],
];

const CORE_FIELDS = ["first_name", "last_name", "email", "gdpr_consent"]; // cannot be deleted; required

export default function FormBuilder() {
  const dialog = useDialog();
  const { currentEvent, isArchived } = useEvents();
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null); // field id currently being expanded
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/forms").then(({ data }) => {
      if (!data.items.length) return;
      const f = data.items[0];
      // Resilience: if sections are missing/empty, rebuild them from the fields so
      // no field is ever orphaned/hidden (fixes blank Form Builder).
      if (!f.sections || f.sections.length === 0) {
        const derived = [];
        (f.fields || []).forEach((fld) => {
          const s = fld.section || "General";
          if (!derived.includes(s)) derived.push(s);
        });
        f.sections = derived.length ? derived : ["General"];
      }
      setForm(f);
    });
  }, []);

  const save = async () => {
    await adminApi.patch(`/admin/forms/${form.id}`, {
      fields: form.fields,
      layout: form.layout,
      sections: form.sections,
      title: form.title ?? null,
      subtext: form.subtext ?? null,
      logo_file_id: form.logo_file_id ?? null,
      background_color: form.background_color ?? null,
      background_image_file_id: form.background_image_file_id ?? null,
    });
    setSaved(true); setTimeout(() => setSaved(false), 2500);
  };

  if (!form) return <AdminLayout title="Form Builder"><Loader /></AdminLayout>;

  const updateField = (idx, patch) => {
    const fields = [...form.fields];
    fields[idx] = { ...fields[idx], ...patch };
    setForm({ ...form, fields });
  };

  const addField = (section) => {
    const id = `field_${Date.now()}`;
    setForm({ ...form, fields: [...form.fields, { id, type: "short_text", label: "New Field", required: false, section }] });
    setEditing(id);
  };

  const requestRemoveField = async (idx) => {
    const f = form.fields[idx];
    if (CORE_FIELDS.includes(f.id)) {
      await dialog.alert({ title: "Core field locked", description: `"${f.label}" is a required system field and cannot be deleted.` });
      return;
    }
    if (await dialog.confirm({
      title: "Delete this question?",
      description: `"${f.label}" will be removed from the form. This cannot be undone.`,
      confirmLabel: "Delete",
      destructive: true,
    })) {
      setForm({ ...form, fields: form.fields.filter((_, i) => i !== idx) });
    }
  };

  const moveField = (idx, dir) => {
    const fields = [...form.fields];
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= fields.length) return;
    [fields[idx], fields[tgt]] = [fields[tgt], fields[idx]];
    setForm({ ...form, fields });
  };

  const addSection = async () => {
    const name = await dialog.prompt({ title: "Add new section", description: "What should this section be called?", placeholder: "e.g. Experience", confirmLabel: "Add Section" });
    if (!name) return;
    if ((form.sections || []).includes(name)) {
      await dialog.alert({ title: "Section already exists", description: `"${name}" is already a section. Try a different name.` });
      return;
    }
    setForm({ ...form, sections: [...(form.sections || []), name] });
  };

  const renameSection = async (oldName) => {
    const name = await dialog.prompt({ title: "Rename section", description: "Enter a new name for this section.", defaultValue: oldName, confirmLabel: "Rename" });
    if (!name || name === oldName) return;
    const sections = (form.sections || []).map((s) => s === oldName ? name : s);
    const fields = form.fields.map((f) => f.section === oldName ? { ...f, section: name } : f);
    setForm({ ...form, sections, fields });
  };

  const removeSection = async (name) => {
    const fieldsInSec = form.fields.filter((f) => f.section === name);
    if (fieldsInSec.length > 0) {
      await dialog.alert({ title: "Section not empty", description: `Move or delete the ${fieldsInSec.length} field(s) in this section first.` });
      return;
    }
    if (await dialog.confirm({ title: "Remove this section?", description: `"${name}" will be removed from the form.`, confirmLabel: "Remove", destructive: true })) {
      setForm({ ...form, sections: (form.sections || []).filter((s) => s !== name) });
    }
  };

  const moveSection = (idx, dir) => {
    const sections = [...(form.sections || [])];
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= sections.length) return;
    [sections[idx], sections[tgt]] = [sections[tgt], sections[idx]];
    setForm({ ...form, sections });
  };

  // All custom profile tabs across the form (Change 05)
  const allProfileTabs = (() => {
    if (!form) return [];
    const set = new Set();
    (form.fields || []).forEach((f) => { if (f.profile_tab) set.add(f.profile_tab); });
    return Array.from(set);
  })();

  const assignTab = async (idx) => {
    let chosen;
    if (allProfileTabs.length === 0) {
      const name = await dialog.prompt({ title: "Create profile tab", description: "What should this tab be called on the applicant profile page?", placeholder: "e.g. Bio, Experience, Media", confirmLabel: "Create Tab" });
      if (!name) return;
      chosen = name.trim();
    } else {
      const res = await dialog.choose({
        title: "Assign to profile tab",
        description: "Pick an existing tab or create a new one. Answers assigned to a tab appear together under that tab on the applicant profile page.",
        options: allProfileTabs.map((t) => ({ value: t, label: t })),
        showCreateOption: true,
        createOptionLabel: "Create new tab",
      });
      if (!res) return;
      if (res.kind === "select") chosen = res.value;
      else if (res.kind === "create") {
        const name = await dialog.prompt({ title: "Create profile tab", description: "What should this tab be called?", placeholder: "e.g. Bio, Experience, Media", confirmLabel: "Create Tab" });
        if (!name) return;
        chosen = name.trim();
      }
    }
    if (chosen) updateField(idx, { profile_tab: chosen });
  };

  const unassignTab = (idx) => updateField(idx, { profile_tab: null });


  // group fields by section — union of declared sections + any section referenced by a field
  const declaredSections = form.sections && form.sections.length ? form.sections : ["General"];
  const allSectionNames = [...declaredSections];
  form.fields.forEach((f) => { const s = f.section || declaredSections[0]; if (!allSectionNames.includes(s)) allSectionNames.push(s); });
  const sectionGroups = allSectionNames.map((s) => ({
    name: s,
    fields: form.fields.map((f, idx) => ({ ...f, _idx: idx })).filter((f) => (f.section || declaredSections[0]) === s),
  }));

  return (
    <AdminLayout
      title="Form Builder"
      subtitle={`${form.name} · drag fields to reorder · changes go live after Save`}
      actions={
        <div className="flex items-center gap-2">
          <a href={currentEvent?.slug ? `/apply/${currentEvent.slug}` : "/apply"} target="_blank" rel="noopener noreferrer" data-testid="form-preview-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood">
            <ExternalLink size={14} /> Preview
          </a>
          {!isArchived && (
            <button onClick={save} data-testid="form-save-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">
              <Save size={14} /> Save Form
            </button>
          )}
        </div>
      }
    >
      {saved && <div className="mb-4 p-3 border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981] text-sm flex items-center gap-2"><Check size={14} /> Form saved — changes are live on the public application page.</div>}

      <div className="grid lg:grid-cols-4 gap-4">
        {/* Settings + sections panel */}
        <div className="lg:col-span-1 space-y-4">
          <Card title="Form Settings">
            <label className="label-mini block mb-1.5">Layout</label>
            <select value={form.layout} onChange={(e) => setForm({...form, layout: e.target.value})} data-testid="form-layout" className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm">
              <option value="multi-step">Multi-step (paginated)</option>
              <option value="single">Single page (all at once)</option>
            </select>
          </Card>

          <Card title="Form Title & Subtext">
            <label className="label-mini block mb-1.5">Title</label>
            <input
              data-testid="form-title-input"
              value={form.title || ""}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Scare Actor Application"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood mb-3"
            />
            <label className="label-mini block mb-1.5">Subtext / Description</label>
            <textarea
              data-testid="form-subtext-input"
              rows={3}
              value={form.subtext || ""}
              onChange={(e) => setForm({ ...form, subtext: e.target.value })}
              placeholder="e.g. Apply to perform at Stourbridge Scare Maze · Season 2026"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood resize-none"
            />
            <p className="text-[10px] text-[#6B6862] mt-1">Both fields are optional — leave blank to hide that element on the public form.</p>
          </Card>

          <BrandingCard form={form} setForm={setForm} dialog={dialog} />

          <Card title="Sections" action={<button onClick={addSection} data-testid="add-section-btn" className="text-xs text-blood hover:underline">+ Add</button>}>
            <div className="space-y-1.5">
              {(form.sections || []).map((s, i) => (
                <div key={s} className="flex items-center gap-1 px-2 py-1.5 border border-[#1a1a1a] bg-[#050505]">
                  <button onClick={() => moveSection(i, -1)} className="text-[#6B6862] hover:text-blood"><ChevronUp size={12} /></button>
                  <button onClick={() => moveSection(i, 1)} className="text-[#6B6862] hover:text-blood"><ChevronDown size={12} /></button>
                  <button onClick={() => renameSection(s)} className="flex-1 text-left text-sm truncate hover:text-blood" title="Click to rename">{s}</button>
                  <button onClick={() => removeSection(s)} className="text-[#6B6862] hover:text-blood"><X size={12} /></button>
                </div>
              ))}
              {(form.sections || []).length === 0 && <p className="text-xs text-[#6B6862]">No sections yet.</p>}
            </div>
          </Card>

          <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-4">
            <h4 className="label-mini mb-2 flex items-center gap-1.5"><AlertCircle size={11} className="text-blood" /> Core Fields</h4>
            <p className="text-xs text-[#A19D94] leading-relaxed">
              <span className="font-mono text-blood">first_name</span>, <span className="font-mono text-blood">last_name</span>, <span className="font-mono text-blood">email</span>, and one consent checkbox (<span className="font-mono text-blood">gdpr_consent</span>) are required by the system and cannot be deleted.
            </p>
          </div>
        </div>

        {/* Fields editor */}
        <div className="lg:col-span-3 space-y-4">
          {sectionGroups.map((sec) => (
            <div key={sec.name} className="border border-[#1a1a1a] bg-[#0A0A0A]">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a]">
                <div>
                  <h3 className="font-display text-lg uppercase tracking-tight">{sec.name}</h3>
                  <p className="text-[10px] text-[#6B6862] font-mono uppercase tracking-wider">{sec.fields.length} field{sec.fields.length === 1 ? "" : "s"}</p>
                </div>
                <button onClick={() => addField(sec.name)} data-testid={`add-field-${sec.name}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">
                  <Plus size={12} /> Add Field
                </button>
              </div>
              <div className="divide-y divide-[#1a1a1a]">
                {sec.fields.length === 0 && <p className="px-4 py-6 text-sm text-[#6B6862]">No fields in this section yet.</p>}
                {sec.fields.map((f) => {
                  const isCore = CORE_FIELDS.includes(f.id);
                  const isOpen = editing === f.id;
                  return (
                    <div key={f.id} className="px-4 py-3" data-testid={`field-row-${f.id}`}>
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col">
                          <button onClick={() => moveField(f._idx, -1)} className="text-[#6B6862] hover:text-blood"><ChevronUp size={12} /></button>
                          <button onClick={() => moveField(f._idx, 1)} className="text-[#6B6862] hover:text-blood"><ChevronDown size={12} /></button>
                        </div>
                        <GripVertical size={14} className="text-[#3a3835]" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate">{f.label}</span>
                            {f.required && <span className="text-[9px] text-blood font-mono uppercase">Required</span>}
                            {isCore && <span className="text-[9px] text-[#F59E0B] font-mono uppercase">Core</span>}
                            {f.profile_tab && <span className="text-[9px] text-[#8B5CF6] font-mono uppercase tracking-wider inline-flex items-center gap-1"><Tag size={9} />{f.profile_tab}</span>}
                          </div>
                          <div className="flex items-center gap-3 text-[10px] font-mono text-[#6B6862] uppercase tracking-wider mt-0.5">
                            <span>{FIELD_TYPES.find(([t]) => t === f.type)?.[1] || f.type}</span>
                            <span className="text-[#3a3835]">·</span>
                            <span>{f.id}</span>
                          </div>
                        </div>
                        <button onClick={() => setEditing(isOpen ? null : f.id)} data-testid={`edit-field-${f.id}`} className="px-3 py-1.5 text-xs border border-[#262626] hover:border-blood hover:text-blood">
                          {isOpen ? "Close" : "Edit"}
                        </button>
                        {!isCore && (
                          <button onClick={() => requestRemoveField(f._idx)} data-testid={`del-field-${f.id}`} className="p-1.5 text-[#6B6862] hover:text-blood"><Trash2 size={14} /></button>
                        )}
                      </div>

                      {isOpen && (
                        <div className="mt-3 ml-6 grid md:grid-cols-2 gap-3 p-3 border border-[#1a1a1a] bg-[#050505]">
                          <I label="Field Label" value={f.label} onChange={(v) => updateField(f._idx, { label: v })} testid={`fld-label-${f.id}`} />
                          <div>
                            <label className="label-mini block mb-1.5">Field Type</label>
                            <select disabled={isCore} value={f.type} onChange={(e) => updateField(f._idx, { type: e.target.value })} data-testid={`fld-type-${f.id}`}
                              className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm disabled:opacity-60">
                              {FIELD_TYPES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            {isCore && <p className="text-[10px] text-[#6B6862] mt-1">Type locked for core field.</p>}
                          </div>
                          <div>
                            <label className="label-mini block mb-1.5">Section</label>
                            <select value={f.section || ""} onChange={(e) => updateField(f._idx, { section: e.target.value })}
                              className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm">
                              {(form.sections || []).map(s => <option key={s}>{s}</option>)}
                            </select>
                          </div>
                          <div className="md:col-span-2">
                            <label className="label-mini block mb-1.5">Profile Answer Display</label>
                            <select
                              data-testid={`fld-display-${f.id}`}
                              value={f.profile_display || "auto"}
                              onChange={(e) => updateField(f._idx, { profile_display: e.target.value === "auto" ? null : e.target.value })}
                              className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm"
                            >
                              <option value="auto">Auto (smart default for this type)</option>
                              <option value="inline">Inline — answer on the same line as the question</option>
                              <option value="new_line">New line — answer on its own line below the label</option>
                            </select>
                            <p className="text-[10px] text-[#6B6862] mt-1">Controls how this answer is laid out on the applicant profile page. Short answers default to inline; long answers default to new line.</p>
                          </div>
                          <div className="md:col-span-2">
                            <label className="label-mini block mb-1.5">Display Answer in Profile Tab</label>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => assignTab(f._idx)} data-testid={`assign-tab-${f.id}`}
                                className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-blood hover:text-blood transition-colors">
                                <Tag size={12} />
                                {f.profile_tab ? `Tab: ${f.profile_tab}` : "Assign Tab"}
                              </button>
                              {f.profile_tab && (
                                <button type="button" onClick={() => unassignTab(f._idx)} className="text-xs text-[#6B6862] hover:text-blood underline">
                                  Remove from tab
                                </button>
                              )}
                            </div>
                            <p className="text-[10px] text-[#6B6862] mt-1.5">When assigned, this answer appears on its own tab in the applicant profile (e.g. "Bio"). Leave unassigned to keep it under the main Profile tab.</p>
                          </div>
                          <I label="Placeholder / Hint" value={f.placeholder || ""} onChange={(v) => updateField(f._idx, { placeholder: v })} />
                          <div className="md:col-span-2">
                            <I label="Helper Text (shown below the field)" value={f.helper || ""} onChange={(v) => updateField(f._idx, { helper: v })} />
                          </div>
                          {f.type === "long_text" && (
                            <div>
                              <label className="label-mini block mb-1.5">Input Height (lines)</label>
                              <input type="number" min={2} max={20} value={f.rows || 4}
                                onChange={(e) => updateField(f._idx, { rows: Math.max(2, parseInt(e.target.value) || 4) })}
                                data-testid={`fld-rows-${f.id}`}
                                className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
                              <p className="text-[10px] text-[#6B6862] mt-1">How tall the text box appears on the form (minimum 2).</p>
                            </div>
                          )}
                          {(f.type === "dropdown" || f.type === "multiple_choice") && (
                            <div className="md:col-span-2">
                              <label className="label-mini block mb-1.5">Options (one per line)</label>
                              <textarea rows={4} value={(f.options || []).join("\n")} onChange={(e) => updateField(f._idx, { options: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
                                data-testid={`fld-options-${f.id}`}
                                className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-blood resize-none" />
                            </div>
                          )}
                          {f.type === "checkbox" && (
                            <div className="md:col-span-2">
                              <I label="Checkbox text (shown beside the box)" value={f.checkbox_label || f.label} onChange={(v) => updateField(f._idx, { checkbox_label: v })} />
                            </div>
                          )}
                          {f.type === "text_block" && (
                            <div className="md:col-span-2">
                              <label className="label-mini block mb-1.5">Text content (shown on the form)</label>
                              <textarea rows={4} value={f.content || ""} onChange={(e) => updateField(f._idx, { content: e.target.value })}
                                data-testid={`fld-content-${f.id}`}
                                placeholder="e.g. Please read the following before completing this section…"
                                className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood resize-none" />
                              <p className="text-[10px] text-[#6B6862] mt-1">Plain text only. Line breaks are preserved.</p>
                            </div>
                          )}
                          <label className="flex items-center gap-2 text-xs text-[#A19D94] md:col-span-2">
                            <input type="checkbox" disabled={isCore || f.type === "text_block"} checked={!!f.required} onChange={(e) => updateField(f._idx, { required: e.target.checked })} className="accent-[#D91616]" />
                            Required field {isCore && <span className="text-[10px] text-[#6B6862] ml-2">(locked for core field)</span>}{f.type === "text_block" && <span className="text-[10px] text-[#6B6862] ml-2">(not applicable for text blocks)</span>}
                          </label>

                          {/* Live preview */}
                          <div className="md:col-span-2 mt-2 p-3 border border-dashed border-[#262626]">
                            <div className="label-mini mb-2">Live Preview</div>
                            {f.type !== "checkbox" && f.type !== "text_block" && <label className="label-mini block mb-1.5">{f.label}{f.required && " *"}</label>}
                            <FieldRenderer field={f} value={undefined} onChange={() => {}} showDates={[]} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}

const I = ({ label, value, onChange, testid }) => (
  <div>
    <label className="label-mini block mb-1.5">{label}</label>
    <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
  </div>
);


function BrandingCard({ form, setForm, dialog }) {
  const [uploading, setUploading] = useState(null); // 'logo' | 'bg' | null

  const uploadFile = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/public/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "logo") setForm((f) => ({ ...f, logo_file_id: data.file_id }));
      else setForm((f) => ({ ...f, background_image_file_id: data.file_id }));
    } catch (e) {
      await dialog.alert({ title: "Upload failed", description: e?.response?.data?.detail || "Could not upload file." });
    } finally { setUploading(null); }
  };

  const clearLogo = () => setForm({ ...form, logo_file_id: null });
  const clearBgImage = () => setForm({ ...form, background_image_file_id: null });
  const clearBgColor = () => setForm({ ...form, background_color: null });

  return (
    <Card title="Branding">
      {/* Logo */}
      <div className="mb-4">
        <label className="label-mini block mb-1.5">Form Logo (centered at top)</label>
        {form.logo_file_id ? (
          <div className="flex items-center gap-2 p-2 border border-[#262626] bg-[#050505]">
            <img src={fileUrl(form.logo_file_id)} alt="logo" className="h-12 w-12 object-contain bg-[#0A0A0A] p-1" />
            <span className="text-xs text-[#A19D94] flex-1 truncate">Logo uploaded</span>
            <button onClick={clearLogo} data-testid="branding-clear-logo" className="text-[#6B6862] hover:text-blood"><X size={14} /></button>
          </div>
        ) : (
          <label className="flex items-center gap-2 border border-dashed border-[#3a3835] hover:border-blood p-3 cursor-pointer transition-colors">
            <Upload size={14} className="text-[#A19D94]" />
            <span className="text-xs flex-1">{uploading === "logo" ? "Uploading…" : "Upload PNG / JPG / SVG"}</span>
            <input data-testid="branding-logo-input" type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={(e) => uploadFile("logo", e.target.files?.[0])} className="hidden" />
          </label>
        )}
      </div>

      {/* Background image */}
      <div className="mb-4">
        <label className="label-mini block mb-1.5 flex items-center gap-1.5"><ImageIcon size={11} /> Background Image</label>
        {form.background_image_file_id ? (
          <div className="space-y-2">
            <div
              className="h-20 border border-[#262626]"
              style={{ backgroundImage: `url(${fileUrl(form.background_image_file_id)})`, backgroundSize: "cover", backgroundPosition: "center" }}
            />
            <button onClick={clearBgImage} data-testid="branding-clear-bg-image" className="text-xs text-blood hover:underline">Remove background image</button>
          </div>
        ) : (
          <label className="flex items-center gap-2 border border-dashed border-[#3a3835] hover:border-blood p-3 cursor-pointer transition-colors">
            <Upload size={14} className="text-[#A19D94]" />
            <span className="text-xs flex-1">{uploading === "bg" ? "Uploading…" : "Upload JPG / PNG / WEBP"}</span>
            <input data-testid="branding-bg-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => uploadFile("bg", e.target.files?.[0])} className="hidden" />
          </label>
        )}
        {form.background_image_file_id && (
          <p className="text-[10px] text-[#F59E0B] mt-1 flex items-center gap-1"><AlertCircle size={10} /> Image overrides the colour below.</p>
        )}
      </div>

      {/* Background colour */}
      <div>
        <label className="label-mini block mb-1.5 flex items-center gap-1.5"><Palette size={11} /> Background Colour</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            data-testid="branding-bg-color"
            value={form.background_color || "#050505"}
            onChange={(e) => setForm({ ...form, background_color: e.target.value })}
            className="w-12 h-10 border border-[#262626] cursor-pointer bg-transparent"
          />
          <input
            type="text"
            value={form.background_color || ""}
            onChange={(e) => setForm({ ...form, background_color: e.target.value })}
            placeholder="#050505"
            className="flex-1 bg-[#050505] border border-[#262626] px-3 py-2 text-sm font-mono focus:outline-none focus:border-blood"
          />
          {form.background_color && (
            <button onClick={clearBgColor} className="p-2 text-[#6B6862] hover:text-blood"><X size={14} /></button>
          )}
        </div>
        <p className="text-[10px] text-[#6B6862] mt-1">Used when no background image is set.</p>
      </div>
    </Card>
  );
}
